import { memo, useCallback } from "react";
import { BottomSheetBackdrop, BottomSheetModal, BottomSheetView } from "@gorhom/bottom-sheet";
import type { BottomSheetBackdropProps } from "@gorhom/bottom-sheet";
import { useUnistyles } from "react-native-unistyles";
import { View, Text } from "@src/components";
import { ManageOptionsList, type ManageOption } from "./ManageOptionsList";
import type { FC } from "react";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { router } from "expo-router";

type ManageAlarmSettingsBottomSheetProps = {
  bottomSheetRef: React.RefObject<BottomSheetModal | null>;
};

export type AlarmSettingsOption = "routines" | "exceptions" | "response" | "notifications";

const renderBackdrop = (props: BottomSheetBackdropProps) => (
  <BottomSheetBackdrop {...props} disappearsOnIndex={-1} appearsOnIndex={0} />
);

export const ManageAlarmSettingsBottomSheet: FC<ManageAlarmSettingsBottomSheetProps> = memo(({ bottomSheetRef }) => {
  const { theme } = useUnistyles();
  const { bottom } = useSafeAreaInsets();

  const handleManageRoutines = useCallback(() => {
    bottomSheetRef.current?.dismiss({ duration: 200 });
    setTimeout(() => {
      router.push("/alarm-settings/manage-routines");
    }, 200);
  }, [bottomSheetRef]);

  const handleManageExceptions = useCallback(() => {
    bottomSheetRef.current?.dismiss({ duration: 200 });
    setTimeout(() => {
      router.push("/alarm-settings/manage-exceptions");
    }, 200);
  }, [bottomSheetRef]);

  const handleManageAlarmResponse = useCallback(() => {
    bottomSheetRef.current?.dismiss({ duration: 200 });
    setTimeout(() => {
      router.push("/alarm-settings/manage-alarm-response");
    }, 200);
  }, [bottomSheetRef]);

  const handleManageNotifications = useCallback(() => {
    bottomSheetRef.current?.dismiss({ duration: 200 });
    setTimeout(() => {
      router.push("/alarm-settings/manage-notifications");
    }, 200);
  }, [bottomSheetRef]);

  const OPTIONS: (ManageOption & { id: AlarmSettingsOption })[] = [
    {
      id: "routines",
      title: "Routines",
      description: "Manage recurring arming and disarming schedules.",
      icon: "calendarRefresh",
      onPress: handleManageRoutines,
    },
    {
      id: "exceptions",
      title: "Exceptions",
      description: "Schedule temporary overrides for holidays or maintenance.",
      icon: "calendarEvent",
      onPress: handleManageExceptions,
    },
    {
      id: "response",
      title: "Alarm response",
      description: "Configure sirens and strobes.",
      icon: "sirenDown",
      onPress: handleManageAlarmResponse,
    },
    {
      id: "notifications",
      title: "Notifications",
      description: "Send email or SMS notifications when an alarm is activated.",
      icon: "bellOutline",
      onPress: handleManageNotifications,
    },
  ];

  return (
    <BottomSheetModal
      ref={bottomSheetRef}
      enableDynamicSizing
      enablePanDownToClose
      backgroundStyle={{ backgroundColor: theme.colors.surfaceCard }}
      backdropComponent={renderBackdrop}
      handleIndicatorStyle={{ backgroundColor: theme.colors.field.border_default, width: 46 }}
      handleStyle={{ paddingTop: theme.spacing["6xl"] }}
    >
      <BottomSheetView
        style={{ paddingHorizontal: theme.spacing["5xl"], paddingBottom: bottom, paddingTop: theme.spacing["5xl"] }}
      >
        <Text textStyle="md/medium" mb="md">
          Manage alarm settings
        </Text>
        <ManageOptionsList options={OPTIONS} />
      </BottomSheetView>
    </BottomSheetModal>
  );
});

ManageAlarmSettingsBottomSheet.displayName = "ManageAlarmSettingsBottomSheet";
